// The person's Provider Keys and own profiles on this device (ADR 0016's amendment of 2026-11-02,
// brief 47). A Provider Key is a credential for one connection, the adapter kind and its endpoint,
// under a name the person gives it. A project's profile finds its key by its connection: one key,
// that one; several, the one the person picked for that profile; none, the pane asks. Nothing here
// enters a project file.
//
// Where the keys live:
// - In a browser, this origin's storage: the list under `interplae.providerKeys` and each value
//   under `interplae.providerKey.<id>`, in local storage, or in session storage for "this session
//   only". The page hands the value to the engine for the session, as before.
// - In the desktop app, the runtime: one keychain item per key and a listing in its support folder
//   (crates/interplae-native/src/keys.rs). The page names a key to the runtime by its id and never
//   reads a value back.
// The choice among several and the person's own profiles are local storage in both.

import type { MachineProfileDraft } from '../engine/protocol.ts'

export interface ProviderKey {
  id: string
  name: string
  kind: string
  endpoint: string
  /// Kept for this browser session alone; never in the desktop app.
  sessionOnly?: boolean
}

/// A key as the Settings sheet saves it, without its value.
export interface KeyDraft {
  id?: string
  name: string
  kind: string
  endpoint: string
}

export interface Connection {
  kind: string
  endpoint: string
}

export const KEYS_LIST = 'interplae.providerKeys'
export const keyValueName = (id: string) => `interplae.providerKey.${id}`
export const choiceName = (profile: string) => `interplae.providerKeyChoice.${profile}`
export const MY_PROFILES = 'interplae.machineProfiles'

/// An endpoint as connections compare it: trimmed, without a trailing slash.
export const endpointOf = (endpoint: string) => endpoint.trim().replace(/\/+$/, '')

export const sameConnection = (a: Connection, b: Connection) => a.kind === b.kind && endpointOf(a.endpoint) === endpointOf(b.endpoint)

/// The key a profile uses (decision 3): the one key for its connection; the one the person picked,
/// while it is still there; the keys to choose among; or none.
export type Found = { kind: 'key'; key: ProviderKey } | { kind: 'choose'; keys: ProviderKey[] } | { kind: 'none' }

export function lookup(keys: readonly ProviderKey[], profile: Connection, chosen: string | null): Found {
  const fits = keys.filter(k => sameConnection(k, profile))
  const picked = fits.find(k => k.id === chosen)
  if (picked) return { kind: 'key', key: picked }
  if (fits.length === 1) return { kind: 'key', key: fits[0]! }
  if (fits.length > 1) return { kind: 'choose', keys: fits }
  return { kind: 'none' }
}

/// What `machine.setCredential` carries for a key: its value in a browser, its id in the app.
export type Handed = { credential: string | null } | { key: string }

/// Where the keys live, and what the engine is handed for one.
export interface KeyStore {
  readonly place: 'browser' | 'device'
  list(): Promise<ProviderKey[]>
  /// A new key needs its value; a known id keeps its value when `value` is null, as a rename does.
  save(draft: KeyDraft, value: string | null, sessionOnly: boolean): Promise<ProviderKey>
  remove(id: string): Promise<void>
  handed(id: string): Promise<Handed>
}

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function readList(storage: Store): ProviderKey[] {
  try {
    const parsed = JSON.parse(storage.getItem(KEYS_LIST) ?? '[]') as unknown
    return Array.isArray(parsed) ? (parsed as ProviderKey[]) : []
  } catch {
    return []
  }
}

const writeList = (storage: Store, keys: ProviderKey[]) => (keys.length ? storage.setItem(KEYS_LIST, JSON.stringify(keys)) : storage.removeItem(KEYS_LIST))

function freshId(): string {
  const bytes = new Uint8Array(8)
  crypto.getRandomValues(bytes)
  return `key-${[...bytes].map(b => b.toString(16).padStart(2, '0')).join('')}`
}

/// The keys in this origin's storage.
export function browserKeys(local: Store, session: Store): KeyStore {
  const strip = ({ id, name, kind, endpoint }: ProviderKey): ProviderKey => ({ id, name, kind, endpoint })
  const where = (id: string): Store | null => (readList(local).some(k => k.id === id) ? local : readList(session).some(k => k.id === id) ? session : null)
  return {
    place: 'browser',
    async list() {
      return [...readList(local).map(strip), ...readList(session).map(k => ({ ...strip(k), sessionOnly: true }))]
    },
    async save(draft, value, sessionOnly) {
      const name = draft.name.trim()
      if (!name || !draft.kind) throw new Error('A Provider Key needs a name and an adapter.')
      const was = draft.id ? where(draft.id) : null
      if (draft.id && !was) throw new Error(`No Provider Key ${draft.id} in this browser.`)
      const kept = draft.id && was ? was.getItem(keyValueName(draft.id)) : null
      const secret = value || kept
      if (!secret) throw new Error('A new Provider Key needs its key.')
      const id = draft.id || freshId()
      const key: ProviderKey = { id, name, kind: draft.kind, endpoint: endpointOf(draft.endpoint) }
      const into = sessionOnly ? session : local
      for (const storage of [local, session]) {
        writeList(storage, readList(storage).filter(k => k.id !== id).map(strip))
        storage.removeItem(keyValueName(id))
      }
      writeList(into, [...readList(into).map(strip), key])
      into.setItem(keyValueName(id), secret)
      return sessionOnly ? { ...key, sessionOnly: true } : key
    },
    async remove(id) {
      for (const storage of [local, session]) {
        writeList(storage, readList(storage).filter(k => k.id !== id).map(strip))
        storage.removeItem(keyValueName(id))
      }
    },
    async handed(id) {
      return { credential: local.getItem(keyValueName(id)) ?? session.getItem(keyValueName(id)) }
    },
  }
}

/// The runtime's requests for the keys, which only the desktop app's engine answers.
export type KeysRequest = { type: 'keys.list' } | { type: 'keys.save'; key: KeyDraft; secret: string | null } | { type: 'keys.remove'; id: string }

/// The keys the desktop app's runtime keeps in the keychain.
export function deviceKeys(call: (request: KeysRequest) => Promise<unknown>): KeyStore {
  return {
    place: 'device',
    async list() {
      return ((await call({ type: 'keys.list' })) as { keys: ProviderKey[] }).keys
    },
    async save(draft, value) {
      return ((await call({ type: 'keys.save', key: { ...draft, endpoint: endpointOf(draft.endpoint) }, secret: value })) as { key: ProviderKey }).key
    },
    async remove(id) {
      await call({ type: 'keys.remove', id })
    },
    async handed(id) {
      return { key: id }
    },
  }
}

/// The key the person picked for a profile among several, remembered by the profile's id.
export const chosenKey = (storage: Store, profile: string): string | null => storage.getItem(choiceName(profile))

export function chooseKey(storage: Store, profile: string, key: string | null): void {
  if (key) storage.setItem(choiceName(profile), key)
  else storage.removeItem(choiceName(profile))
}

/// The person's own profiles: what a project copies, without a key.
export function myProfiles(storage: Store): MachineProfileDraft[] {
  try {
    const parsed = JSON.parse(storage.getItem(MY_PROFILES) ?? '[]') as unknown
    return Array.isArray(parsed) ? (parsed as MachineProfileDraft[]) : []
  } catch {
    return []
  }
}

/// Saves one of the person's profiles under its id, or a new one under an id from its name.
export function saveMyProfile(storage: Store, draft: MachineProfileDraft): MachineProfileDraft {
  const all = myProfiles(storage)
  const id = draft.id || freeId(draft.name, new Set(all.map(p => p.id)))
  const saved = { ...draft, id, name: draft.name.trim(), endpoint: draft.endpoint.trim() }
  const next = all.some(p => p.id === id) ? all.map(p => (p.id === id ? saved : p)) : [...all, saved]
  storage.setItem(MY_PROFILES, JSON.stringify(next))
  return saved
}

export function removeMyProfile(storage: Store, id: string): void {
  const next = myProfiles(storage).filter(p => p.id !== id)
  if (next.length) storage.setItem(MY_PROFILES, JSON.stringify(next))
  else storage.removeItem(MY_PROFILES)
}

/// An id from a name, lower case with hyphens, numbered past the ones taken.
export function freeId(name: string, taken: ReadonlySet<string>): string {
  const base = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'profile'
  let id = base
  for (let n = 2; taken.has(id); n++) id = `${base}-${n}`
  return id
}
